import { forwardRef, useCallback, useState } from "react";
import { HexColorPicker } from "react-colorful";
import { cn } from "../lib/utils";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";
import { Input } from "./input";
import { GetColorName } from "hex-color-to-color-name";
import { ChevronDown } from "lucide-react";

const HEX_PATTERN = /^#?([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6}|[0-9A-Fa-f]{8})$/;

const normalizeHex = (value: string): string => {
  const trimmed = value.trim();
  if (!trimmed) return "";
  return trimmed.startsWith("#") ? trimmed.toUpperCase() : `#${trimmed.toUpperCase()}`;
};

export interface ColorPickerProps {
  value?: string;
  onValueChange?: (value: string) => void;
  onBlur?: () => void;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
  id?: string;
  "aria-invalid"?: boolean;
  "aria-describedby"?: string;
  "aria-required"?: boolean;
  "aria-label"?: string;
  showName?: boolean;
}

export const ColorPicker = forwardRef<HTMLButtonElement, ColorPickerProps>(
  (
    { value, onValueChange, onBlur, disabled, placeholder = "#000000", className, id, showName = false, ...ariaProps },
    ref
  ) => {
    const [open, setOpen] = useState(false);
    const [inputDraft, setInputDraft] = useState<string | null>(null);

    const currentValue = value ?? "";
    const pickerColor = HEX_PATTERN.test(currentValue) ? normalizeHex(currentValue) : "#000000";

    const handleOpenChange = useCallback(
      (nextOpen: boolean) => {
        setOpen(nextOpen);
        if (!nextOpen) {
          setInputDraft(null);
          onBlur?.();
        }
      },
      [onBlur]
    );

    const handlePickerChange = useCallback(
      (nextColor: string) => {
        setInputDraft(null);
        onValueChange?.(nextColor.toUpperCase());
      },
      [onValueChange]
    );

    const handleInputChange = useCallback(
      (event: React.ChangeEvent<HTMLInputElement>) => {
        const raw = event.target.value;
        setInputDraft(raw);
        if (HEX_PATTERN.test(raw)) {
          onValueChange?.(normalizeHex(raw));
        }
      },
      [onValueChange]
    );

    const handleInputBlur = useCallback(() => {
      if (inputDraft === null) return;
      if (inputDraft === "") {
        onValueChange?.("");
      }
      setInputDraft(null);
    }, [inputDraft, onValueChange]);

    const inputValue = inputDraft ?? currentValue;

    return (
      <Popover open={open} onOpenChange={handleOpenChange}>
        <PopoverTrigger asChild>
          <button
            ref={ref}
            id={id}
            type="button"
            disabled={disabled}
            data-slot="color-picker-trigger"
            className={cn(
              "flex h-9 w-full items-center gap-2 rounded-md border border-input bg-background px-3 text-sm shadow-xs outline-none transition-colors justify-between",
              "focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1",
              "disabled:cursor-not-allowed disabled:opacity-50",
              "aria-invalid:border-destructive aria-invalid:ring-destructive/20",
              className
            )}
            {...ariaProps}
          >
            <div className="flex gap-2 items-center">
              {!!currentValue && (
                <span
                  data-slot="color-picker-swatch"
                  className="size-3 shrink-0 rounded-full"
                  style={{ backgroundColor: currentValue || "transparent" }}
                />
              )}
              <span className={cn(!currentValue && "text-muted-foreground")}>
                {(showName && currentValue ? `${GetColorName(currentValue)} (${currentValue})` : currentValue) ||
                  placeholder}
              </span>
            </div>
            <ChevronDown size={16} className="text-muted-foreground" />
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-3" align="start" side="bottom">
          <div className="flex flex-col gap-3">
            <HexColorPicker color={pickerColor} onChange={handlePickerChange} />
            <Input
              value={inputValue}
              onChange={handleInputChange}
              onBlur={handleInputBlur}
              placeholder={placeholder}
              className="h-9 font-mono"
              aria-label="Hex color value"
            />
          </div>
        </PopoverContent>
      </Popover>
    );
  }
);

ColorPicker.displayName = "ColorPicker";
